/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { TimeComponentZoomControl } from './TimeComponentZoomControl';
import { MinusIcon } from '#PRIVATE_ThemedIcons/MinusIcon';
import type {
  OnTimeComponentViewportChange,
  TimeComponentZoomParameters
} from './timeComponent.types';
import { getISOString } from './utils/timeUtils';
import { zoomTime, DEFAULT_ZOOM_OUT_FACTOR } from './utils/timeZoomUtils';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '#resources/nls/bundle';

type Props = {
  zoomFactor?: number;
  startTime: number;
  endTime: number;
  viewportStartTime: number;
  viewportEndTime: number;
  majorAxisZoomOrder?: TimeComponentZoomParameters['majorAxisZoomOrder'];
  majorAxisZoomOrderIndex?: TimeComponentZoomParameters['majorAxisZoomOrderIndex'];
  minorAxisZoomOrder: TimeComponentZoomParameters['minorAxisZoomOrder'];
  minorAxisZoomOrderIndex: TimeComponentZoomParameters['minorAxisZoomOrderIndex'];
  zoomOrderViewDurationThresholds: ReturnType<
    TimeComponentZoomParameters['getZoomOrderViewDurationThresholds']
  >;
  minZoomViewportDuration: TimeComponentZoomParameters['minZoomViewportDuration'];
  maxZoomViewportDuration: TimeComponentZoomParameters['maxZoomViewportDuration'];
  onViewportChange?: OnTimeComponentViewportChange;
};

export function TimeComponentZoomOutControl({
  zoomFactor = DEFAULT_ZOOM_OUT_FACTOR,
  startTime,
  endTime,
  viewportStartTime,
  viewportEndTime,
  majorAxisZoomOrder,
  majorAxisZoomOrderIndex,
  minorAxisZoomOrder,
  minorAxisZoomOrderIndex,
  zoomOrderViewDurationThresholds,
  minZoomViewportDuration,
  maxZoomViewportDuration,
  onViewportChange
}: Props) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const accessibleLabel = translations.timeComponent_tooltipZoomOut();

  const viewportCenter = viewportStartTime + (viewportEndTime - viewportStartTime) / 2;
  const { newViewport, isViewportChanged } = zoomTime(
    viewportCenter,
    viewportStartTime,
    viewportEndTime,
    startTime,
    endTime,
    zoomFactor,
    minZoomViewportDuration,
    maxZoomViewportDuration,
    zoomOrderViewDurationThresholds,
    minorAxisZoomOrder,
    minorAxisZoomOrderIndex,
    majorAxisZoomOrder,
    majorAxisZoomOrderIndex
  );
  const isDisabled = !isViewportChanged;

  const onAction = () => {
    onViewportChange?.({
      viewportStart: getISOString(newViewport.viewportStartTime),
      viewportEnd: getISOString(newViewport.viewportEndTime),
      majorAxisScale: newViewport.majorAxisScale,
      minorAxisScale: newViewport.minorAxisScale
    });
  };

  return (
    <TimeComponentZoomControl
      accessibleLabel={accessibleLabel}
      isDisabled={isDisabled}
      onAction={onAction}>
      <MinusIcon size="4x" />
    </TimeComponentZoomControl>
  );
}
